/**
 * Portal Login Page — "Ethereal Green" Design
 *
 * Estética adaptada do Login interno do SE_WEB (docs/ALTERACOES_CORRECOES/REDESIGN_TELA_INICIAL),
 * mas com paleta em verde suave (Ponto Banese) e logo própria do parceiro.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { AlertCircle, Eye, EyeOff, HelpCircle, Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import { useMaintenanceContext } from "@/contexts/MaintenanceContext";
import { AvisoDeManutencaoNoLogin } from "@/modules/maintenance/components/AvisoDeManutencaoNoLogin";
import { avisoDeManutencaoNoLogin } from "@/modules/maintenance/utils/avisoDeManutencaoNoLogin";

// ============================================================================
// Validation Schema (permissivo — aceita emails internos com TLD .local)
// ============================================================================

const portalLoginSchema = z.object({
  email: z
    .string()
    .min(1, "Email é obrigatório")
    .max(100, "Email muito longo")
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email inválido"),
  password: z
    .string()
    .min(6, "A senha deve ter no mínimo 6 caracteres")
    .max(100, "Senha muito longa"),
});

type PortalLoginFormData = z.infer<typeof portalLoginSchema>;

const CURRENT_YEAR = new Date().getFullYear();

// ============================================================================
// Component
// ============================================================================

const PortalLogin: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const {
    login,
    isAuthenticated,
    isLoading: authLoading,
    error: authError,
    clearError,
  } = usePortalAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/portal/inicio");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PortalLoginFormData>({
    resolver: zodResolver(portalLoginSchema),
    mode: "onBlur",
  });

  // Aviso GRANDE de manutenção (decisão do Augusto, 28/09/2026), o mesmo do
  // /login: desde a contagem regressiva até o fim da trava. O 503 do SUBMIT
  // vale como "travada" antes de o polling saber (o login do portal usa axios
  // puro, sem o interceptor que invalida o status).
  const manutencao = useMaintenanceContext();
  const [avisoNoSubmit, setAvisoNoSubmit] = useState<string | null>(null);
  const aviso =
    avisoNoSubmit !== null ? { tipo: "travada" as const } : avisoDeManutencaoNoLogin(manutencao);

  // Fim da manutenção limpa o aviso do submit (senão ficaria preso na tela).
  useEffect(() => {
    if (!manutencao.isActive && avisoNoSubmit !== null) {
      setAvisoNoSubmit(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só reage ao fim da manutenção
  }, [manutencao.isActive]);

  const onSubmit = async (data: PortalLoginFormData) => {
    try {
      clearError();
      await login(data.email, data.password);
      setAvisoNoSubmit(null);
    } catch (error) {
      if (error instanceof Error && (error as Error & { manutencao?: boolean }).manutencao) {
        // O aviso grande diz isso; o alerta vermelho repetiria o texto como erro.
        clearError();
        setAvisoNoSubmit(error.message);
        return;
      }
      console.error("Login failed:", error);
    }
  };

  const isLoading = authLoading || isSubmitting;

  return (
    <div className="bg-emerald-50/40 dark:bg-gray-950 font-body text-ep-on-surface dark:text-gray-100 h-dvh overflow-y-auto flex flex-col items-center selection:bg-emerald-100 relative">
      {/* ── Abstract Background Blobs (paleta verde suave) ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -left-[5%] w-[40vw] h-[40vw] rounded-full blur-[120px] bg-emerald-300/35 dark:bg-emerald-500/20" />
        <div className="absolute bottom-[5%] right-[2%] w-[35vw] h-[35vw] rounded-full blur-[100px] bg-green-200/40 dark:bg-green-500/15" />
        <div className="absolute top-[40%] left-[45%] w-[25vw] h-[25vw] rounded-full blur-[90px] bg-teal-200/30 dark:bg-teal-500/10" />
        {/* Watermark — logo Ponto Banese bem sutil */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url(/ponto-banese-logo.png)",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "contain",
            filter: "grayscale(1) brightness(1.6) opacity(0.04)",
            transform: "scale(1.8) rotate(-4deg)",
          }}
        />
      </div>

      {/* ── Asymmetric Gradient Overlay (tons de verde) ── */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(circle at 10% 20%, rgba(167,243,208,0.20) 0%, transparent 42%), " +
            "radial-gradient(circle at 90% 80%, rgba(134,239,172,0.15) 0%, transparent 42%)",
        }}
      />

      {/* ── Top Header (ajuda) ── */}
      <header className="w-full top-0 left-0 bg-transparent flex justify-between items-center px-4 sm:px-8 py-5 max-w-screen-2xl mx-auto z-20">
        <button
          type="button"
          aria-label="Ajuda"
          className="text-emerald-700 dark:text-emerald-300 hover:opacity-70 transition-opacity"
          onClick={() => {
            /* TODO: help modal */
          }}
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </header>

      {/* ── Main Content ── */}
      <main className="flex-grow w-full flex items-center justify-center relative px-4 sm:px-6 z-10">
        <div className="w-full max-w-[460px]">
          {/* ── Glassmorphism Card ── */}
          <div className="bg-white/85 dark:bg-gray-900/85 backdrop-blur-[20px] rounded-xl p-6 sm:p-8 md:p-10 border border-emerald-200/40 dark:border-emerald-900/30 shadow-[0_12px_40px_rgba(16,78,50,0.08)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4)] animate-ep-fade-in">
            {/* Logo Ponto Banese */}
            <div className="flex justify-center mb-6">
              <img
                src="/ponto-banese-logo.png"
                alt="Ponto Banese"
                className="w-24 h-24 sm:w-28 sm:h-28 object-contain drop-shadow-sm"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                  const fallback = (e.target as HTMLImageElement).nextElementSibling;
                  if (fallback) (fallback as HTMLElement).style.display = "flex";
                }}
              />
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 items-center justify-center hidden shadow-lg">
                <span className="text-white font-headline font-extrabold text-2xl sm:text-3xl">
                  PB
                </span>
              </div>
            </div>

            {/* Title */}
            <div className="mb-6 text-center">
              <h1 className="font-headline text-2xl sm:text-3xl font-extrabold tracking-tight text-emerald-800 dark:text-emerald-300 mb-2">
                Portal do Parceiro
              </h1>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                Acesse seus processos, comprovantes e pagamentos
              </p>
            </div>

            {aviso && (
              <AvisoDeManutencaoNoLogin
                tipo={aviso.tipo}
                publico="parceiro"
                mensagem={avisoNoSubmit ?? manutencao.message}
                previsaoDeVolta={manutencao.estimatedEnd}
                fimDaContagem={aviso.tipo === "contagem" ? aviso.fimDaContagem : null}
              />
            )}

            {/* Divisor sutil */}
            <div className="h-px bg-gradient-to-r from-transparent via-emerald-200/60 dark:via-emerald-900/40 to-transparent mb-6" />

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Error Alert */}
              {authError && (
                <Alert
                  variant="destructive"
                  className="border-red-200 bg-red-50/80 dark:bg-red-950/30"
                >
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">{authError}</AlertDescription>
                </Alert>
              )}

              {/* Email */}
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  autoComplete="username"
                  className={`h-11 text-sm bg-white/60 dark:bg-gray-800/60 border-emerald-200/60 dark:border-gray-700 focus-visible:ring-emerald-500 focus-visible:border-emerald-400 ${
                    errors.email ? "border-red-400 focus-visible:ring-red-400" : ""
                  }`}
                  disabled={isLoading}
                  {...register("email")}
                />
                {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
              </div>

              {/* Senha */}
              <div className="space-y-2">
                <Label
                  htmlFor="password"
                  className="text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Digite sua senha"
                    autoComplete="current-password"
                    className={`h-11 text-sm pr-10 bg-white/60 dark:bg-gray-800/60 border-emerald-200/60 dark:border-gray-700 focus-visible:ring-emerald-500 focus-visible:border-emerald-400 ${
                      errors.password ? "border-red-400 focus-visible:ring-red-400" : ""
                    }`}
                    disabled={isLoading}
                    {...register("password")}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-emerald-600 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-500">{errors.password.message}</p>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-md hover:shadow-lg transition-all active:scale-[0.99]"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  "Entrar"
                )}
              </Button>
            </form>

            {/* Help Text */}
            <div className="mt-6 text-center space-y-1">
              <p className="text-xs text-slate-600 dark:text-slate-400">Problemas para acessar?</p>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                Entre em contato com o suporte
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="w-full flex items-center justify-between px-6 sm:px-10 py-4 text-[11px] uppercase tracking-wider text-emerald-900/50 dark:text-emerald-200/40 z-10 flex-shrink-0">
        <span>© {CURRENT_YEAR} Ponto Banese · Portal do Parceiro</span>
        <span className="hidden sm:flex items-center gap-4">
          <span className="hover:text-emerald-800 dark:hover:text-emerald-200 cursor-pointer transition-colors">
            Privacidade
          </span>
          <span className="hover:text-emerald-800 dark:hover:text-emerald-200 cursor-pointer transition-colors">
            Termos
          </span>
          <span className="hover:text-emerald-800 dark:hover:text-emerald-200 cursor-pointer transition-colors">
            Suporte
          </span>
        </span>
      </footer>
    </div>
  );
};

export default PortalLogin;
